import posthog from 'posthog-js'

// ─── Auth ────────────────────────────────────────────────────────────────────
interface AuthLoginSucceeded {
  auth_method: string
}

interface AuthLogoutCompleted {
  [key: string]: never
}

interface AuthLoginCtaClicked {
  source: string
  cta_text: string
}

interface AuthSignupGateShown {
  trigger: string
  page_path: string
}

interface AuthSignupCompleted {
  signup_duration_ms?: number
}

// ─── Onboarding ──────────────────────────────────────────────────────────────
interface OnboardingStarted {
  source: string
}

interface OnboardingOrgCompleted {
  company_size: string
  country: string
}

interface OnboardingQuestionsCompleted {
  question_count: number
  answers_provided: number
}

interface OnboardingSkipped {
  step_name: string
}

interface OnboardingCompleted {
  steps_completed: number
  steps_skipped: number
}

interface OnboardingStepViewed {
  step_name: string
  step_index: number
}

/** The board auto-created for a brand-new workspace at the end of onboarding. */
interface OnboardingFirstBoardCreated {
  board_id: string
}

/** FS-663: an invited recipient finished onboarding on the resource shared with them. */
interface OnboardingLandedOnSharedResource {
  path: string
  /** How many pending shares were competing; >1 means we picked the newest. */
  share_count?: number
}

interface OnboardingStepDropoff {
  step_name: string
  step_index: number
  time_on_step_ms: number
}

// ─── Chat ────────────────────────────────────────────────────────────────────
interface ChatMessageSent {
  message_length: number
  agent_id: string
  context_type?: string
  has_attachments: boolean
  attachment_count: number
}

interface ChatConversationCreated {
  source: string
}

interface ChatConversationDeleted {
  conversation_id: string
}

interface ChatConversationSwitched {
  conversation_id: string
}

interface ChatFeedbackSubmitted {
  message_id: string
  conversation_id: string
  rating: number
  agent_id?: string
}

interface ChatMessageCopied {
  message_id: string
  content_length: number
}

interface ChatAgentSwitched {
  from_agent: string
  to_agent: string
}

// ─── File ────────────────────────────────────────────────────────────────────
interface FileUploadStarted {
  file_type: string
  size_bytes: number
  is_first_upload?: boolean
  upload_method?: 'manual' | 'api' | 'batch' | 'connector'
}

interface FileUploadCompleted {
  file_type: string
  size_bytes: number
  is_first_upload?: boolean
  upload_method?: 'manual' | 'api' | 'batch' | 'connector'
}

interface FileUploadFailed {
  file_type: string
  size_bytes: number
  error_type: string
  is_first_upload?: boolean
  upload_method?: 'manual' | 'api' | 'batch' | 'connector'
}

// ─── Document ────────────────────────────────────────────────────────────────
interface DocumentViewerLoaded {
  document_id: string
  document_type: string
}

interface DocumentSearched {
  query_length: number
}

interface DocumentFiltered {
  filter_type: string
  filter_value: string
}

interface DocumentDownloaded {
  document_id: string
  document_type: string
  export_format?: string
}

interface DocumentDeleted {
  document_ids: string[]
  count: number
  is_bulk: boolean
}

interface DocumentRenamed {
  document_id: string
  document_type?: string
}

interface DocumentProcessingRetried {
  document_id: string
  document_type: string
  retry_count?: number
}

interface DocumentProcessingCompleted {
  document_id: string
  document_type: string
  processing_duration_ms?: number
  is_first_parse?: boolean
}

interface DocumentProcessingFailed {
  document_id: string
  document_type: string
  error_type: string
  is_first_parse?: boolean
}

// ─── Board ───────────────────────────────────────────────────────────────────
interface BoardSearched {
  query_length: number
}

interface BoardCommentPinned {
  board_id: string
}

interface BoardLeftnavToggled {
  /** true when the leftnav was expanded, false when collapsed. */
  expanded: boolean
  /** Board the toggle happened on — matches sibling board-specific events. */
  board_id: string | undefined
}

interface BoardEntityMatchingStarted {
  board_id: string
}

interface BoardPnidHydrated {
  board_id: string | undefined
  /** On-board graph docs that reference a source P&ID/PFD. */
  graph_docs: number
  /** Sources resolved from server-provided inline data (include_pnid_source). */
  resolved_inline: number
  /** Network requests issued to resolve sources (batch chunks + queue fallbacks). */
  network_requests: number
  /** Graph docs that ended up with a resolved source. */
  resolved: number
  /** Graph docs left without a source (stale pnid). */
  unresolved: number
}

// ─── Workspace ───────────────────────────────────────────────────────────────
interface WorkspaceCreated {
  workspace_id: string
}

interface WorkspaceSwitched {
  workspace_id: string
}

/** One-time landing: a user's first arrival goes straight to a board. */
interface WorkspaceFirstLoginLanding {
  workspace_id: string
}

interface TenantSwitched {
  tenant_id: string
}

interface WorkspaceDeleted {
  workspace_id: string
}

interface WorkspaceShared {
  workspace_id: string
  share_method?: 'link' | 'email' | 'export'
}

interface WorkspaceDocumentAdded {
  workspace_id: string
  document_ids: string[]
  document_types: string[]
  count: number
}

interface WorkspaceDocumentRemoved {
  workspace_id: string
  document_ids: string[]
  count: number
}

// ─── Graph ───────────────────────────────────────────────────────────────────
interface GraphNodeCreated {
  document_id: string
  entity_type: string
  source: string
}

interface GraphNodeDeleted {
  node_count: number
  is_bulk: boolean
}

interface GraphNodeSelected {
  entity_type: string
}

interface GraphNodesReclassified {
  node_count: number
}

// ENG-2632: batch fill/clear of a text field (tag / annotation_text / note) across a selection.
interface GraphNodesFieldEdited {
  node_count: number
  field: string
}

interface GraphToolChanged {
  tool_name: string
}

interface GraphSearchExecuted {
  query_length: number
}

interface GraphEntityApproved {
  entity_count: number
}

// ─── Connector ───────────────────────────────────────────────────────────────
interface ConnectorCreated {
  connector_type: string
  connector_id: string
}

interface ConnectorEdited {
  connector_id: string
  connector_type: string
}

interface ConnectorDeleted {
  connector_id: string
  connector_type: string
}

interface ConnectorActivated {
  connector_id: string
  connector_type: string
  is_active: boolean
}

interface ConnectorConfigDownloaded {
  connector_id: string
  connector_type: string
}

// ─── Comment ─────────────────────────────────────────────────────────────────
interface CommentCreated {
  workspace_id: string
  is_reply: boolean
  mention_count: number
  content_length: number
}

interface CommentResolved {
  comment_id: string
  resolved: boolean
}

interface CommentDeleted {
  comment_id: string
}

// ─── Settings ────────────────────────────────────────────────────────────────
interface SettingsTabViewed {
  tab_name: string
}

interface SettingsFeatureToggled {
  flag_name: string
  enabled: boolean
}

interface SettingsTeamMemberInvited {
  role: string
  invite_source?: 'direct' | 'shared_link' | 'admin_panel'
}

type SettingsTeamMemberRemoved = Record<string, never>

// ─── Timeseries ──────────────────────────────────────────────────────────────
interface TimeseriesExplored {
  document_id: string
  tag_count: number
}

interface TimeseriesChatOpened {
  document_id: string
}

// ─── Workflow ────────────────────────────────────────────────────────────────
interface WorkflowQuerySubmitted {
  message_length: number
  model: string
  has_conversation_history: boolean
  is_template_prompt: boolean
}

interface WorkflowCompleted {
  duration_ms: number
  model: string
  step_count: number
  had_clarification: boolean
}

interface WorkflowFailed {
  model: string
  error_type: string
}

interface WorkflowClarificationRequested {
  clarification_type: string
  model: string
}

interface WorkflowCancelled {
  duration_ms: number
  model: string
}

interface WorkflowTemplateUsed {
  template_prompt: string
  model: string
}

// ─── AI Call / Meeting ──────────────────────────────────────────────────────
interface AiCallStarted {
  agent_id?: string
  agent_name?: string
  meeting_platform: string
  digital_coworker_id?: string
  coworker_name?: string
  translator_mode_enabled?: boolean
  // "Use Entire Data Catalog" at call start: the bot retrieves from the whole tenant catalog
  // rather than the meeting's board. Tracked so adoption of the toggle is measurable.
  entire_data_catalog_enabled?: boolean
}

interface AiCallFailed {
  agent_id?: string
  digital_coworker_id?: string
  error_type: string
}

interface AiCallConnected {
  agent_name?: string
  meeting_token_present: boolean
}

interface AiCallDisconnected {
  agent_name?: string
  duration_ms: number
  disconnect_reason?: string
}

interface AiCallMicFailed {
  /** A `MicFailureReason` from `@/lib/micDiagnostics`. */
  reason: string
  /** Where it was detected: preflight | initDevices | no-track | onDeviceError. */
  context: string
}

interface AiCallAgentSelected {
  agent_id: string
  agent_name: string
}

interface MeetingIntegrationLinked {
  platform: string
}

interface MeetingIntegrationUnlinked {
  platform: string
}

// ─── Usage & Limits ──────────────────────────────────────────────────────────
interface UsageLimitApproaching {
  limit_type: string
  current_usage: number
  max_usage: number
  usage_percent: number
}

interface UsageLimitReached {
  limit_type: string
  current_usage: number
  max_usage: number
}

interface FeatureGateHit {
  feature_name: string
  user_tier: string
}

interface PricingPageViewed {
  source: string
  current_tier?: string
}

interface UpgradeStarted {
  from_tier: string
  to_tier: string
  trigger: string
}

interface UpgradeCompleted {
  from_tier: string
  to_tier: string
  plan_interval: string
}

interface PlanSelected {
  plan_name: string
  plan_interval: string
}

// ─── Activation ──────────────────────────────────────────────────────────────
interface FirstActionAfterSignup {
  action_type: string
  time_since_signup_ms: number
}

interface ActivationMilestoneReached {
  milestone: string
  time_since_signup_ms: number
  session_count: number
}

// ─── Page Engagement ─────────────────────────────────────────────────────────
interface PageScrollDepth {
  page_path: string
  max_scroll_percent: number
  time_on_page_ms: number
}

interface PageEngagement {
  page_path: string
  time_on_page_ms: number
  scroll_depth_percent: number
  interaction_count: number
}

interface VideoPlayed {
  video_id: string
  page_path: string
}

interface VideoSkipped {
  video_id: string
  page_path: string
  time_watched_ms: number
  video_duration_ms?: number
}

// ─── Parse Result Quality (PLG Funnel) ──────────────────────────────────────
interface ParseResultRated {
  document_id: string
  document_type: string
  rating: number
  is_first_parse?: boolean
}

interface ParseResultExported {
  document_id: string
  document_type: string
  export_format: string
  destination?: string
}

interface GraphLabelledExported {
  document_id: string
  symbols: number
  lines: number
  tags: number
}

// ─── Event Map ───────────────────────────────────────────────────────────────
export interface AnalyticsEventMap {
  // Auth
  'auth-login_succeeded': AuthLoginSucceeded
  'auth-logout_completed': AuthLogoutCompleted
  'tenant-switched': TenantSwitched
  'auth-login_cta_clicked': AuthLoginCtaClicked
  'auth-signup_gate_shown': AuthSignupGateShown
  'auth-signup_completed': AuthSignupCompleted
  // Onboarding
  'onboarding-started': OnboardingStarted
  'onboarding-org_completed': OnboardingOrgCompleted
  'onboarding-questions_completed': OnboardingQuestionsCompleted
  'onboarding-skipped': OnboardingSkipped
  'onboarding-completed': OnboardingCompleted
  'onboarding-step_viewed': OnboardingStepViewed
  'onboarding-step_dropoff': OnboardingStepDropoff
  'onboarding-first_board_created': OnboardingFirstBoardCreated
  'onboarding-landed_on_shared_resource': OnboardingLandedOnSharedResource
  // Chat
  'chat-message_sent': ChatMessageSent
  'chat-conversation_created': ChatConversationCreated
  'chat-conversation_deleted': ChatConversationDeleted
  'chat-conversation_switched': ChatConversationSwitched
  'chat-feedback_submitted': ChatFeedbackSubmitted
  'chat-message_copied': ChatMessageCopied
  'chat-agent_switched': ChatAgentSwitched
  // File
  'file-upload_started': FileUploadStarted
  'file-upload_completed': FileUploadCompleted
  'file-upload_failed': FileUploadFailed
  // Document
  'document-viewer_loaded': DocumentViewerLoaded
  'document-searched': DocumentSearched
  'document-filtered': DocumentFiltered
  'document-downloaded': DocumentDownloaded
  'document-deleted': DocumentDeleted
  'document-renamed': DocumentRenamed
  'document-processing_retried': DocumentProcessingRetried
  'document-processing_completed': DocumentProcessingCompleted
  'document-processing_failed': DocumentProcessingFailed
  // Board
  'board-searched': BoardSearched
  'board-comment_pinned': BoardCommentPinned
  'board-leftnav_toggled': BoardLeftnavToggled
  'board-entity_matching_started': BoardEntityMatchingStarted
  'board-pnid_hydrated': BoardPnidHydrated
  // Workspace
  'workspace-created': WorkspaceCreated
  'workspace-switched': WorkspaceSwitched
  'workspace-first_login_landing': WorkspaceFirstLoginLanding
  'workspace-deleted': WorkspaceDeleted
  'workspace-shared': WorkspaceShared
  'workspace-document_added': WorkspaceDocumentAdded
  'workspace-document_removed': WorkspaceDocumentRemoved
  // Graph
  'graph-node_created': GraphNodeCreated
  'graph-node_deleted': GraphNodeDeleted
  'graph-node_selected': GraphNodeSelected
  'graph-nodes_reclassified': GraphNodesReclassified
  'graph-nodes_field_filled': GraphNodesFieldEdited
  'graph-nodes_field_cleared': GraphNodesFieldEdited
  'graph-tool_changed': GraphToolChanged
  'graph-search_executed': GraphSearchExecuted
  'graph-entity_approved': GraphEntityApproved
  'graph-labelled_exported': GraphLabelledExported
  // Connector
  'connector-created': ConnectorCreated
  'connector-edited': ConnectorEdited
  'connector-deleted': ConnectorDeleted
  'connector-activated': ConnectorActivated
  'connector-config_downloaded': ConnectorConfigDownloaded
  // Comment
  'comment-created': CommentCreated
  'comment-resolved': CommentResolved
  'comment-deleted': CommentDeleted
  // Settings
  'settings-tab_viewed': SettingsTabViewed
  'settings-feature_toggled': SettingsFeatureToggled
  'settings-team_member_invited': SettingsTeamMemberInvited
  'settings-team_member_removed': SettingsTeamMemberRemoved
  // Timeseries
  'timeseries-explored': TimeseriesExplored
  'timeseries-chat_opened': TimeseriesChatOpened
  // Workflow
  'workflow-query_submitted': WorkflowQuerySubmitted
  'workflow-completed': WorkflowCompleted
  'workflow-failed': WorkflowFailed
  'workflow-clarification_requested': WorkflowClarificationRequested
  'workflow-cancelled': WorkflowCancelled
  'workflow-template_used': WorkflowTemplateUsed
  // AI Call / Meeting
  'ai_call-started': AiCallStarted
  'ai_call-failed': AiCallFailed
  'ai_call-connected': AiCallConnected
  'ai_call-disconnected': AiCallDisconnected
  'ai_call-mic_failed': AiCallMicFailed
  'ai_call-agent_selected': AiCallAgentSelected
  'meeting-integration_linked': MeetingIntegrationLinked
  'meeting-integration_unlinked': MeetingIntegrationUnlinked
  // Usage & Limits
  'usage-limit_approaching': UsageLimitApproaching
  'usage-limit_reached': UsageLimitReached
  'usage-feature_gate_hit': FeatureGateHit
  'pricing-page_viewed': PricingPageViewed
  'upgrade-started': UpgradeStarted
  'upgrade-completed': UpgradeCompleted
  'upgrade-plan_selected': PlanSelected
  // Activation
  'activation-first_action': FirstActionAfterSignup
  'activation-milestone_reached': ActivationMilestoneReached
  // Parse Result Quality (PLG Funnel)
  'parse-result_rated': ParseResultRated
  'parse-result_exported': ParseResultExported
  // Page Engagement
  'page-scroll_depth': PageScrollDepth
  'page-engagement': PageEngagement
  'video-played': VideoPlayed
  'video-skipped': VideoSkipped
  // Help agent ("Akeera", components/HelpAgent)
  'help_agent-opened': HelpAgentOpened
  'help_agent-feedback_submitted': HelpAgentFeedbackSubmitted
  'help_agent-tour_started': HelpAgentTourEvent
  'help_agent-tour_completed': HelpAgentTourEvent
  'help_agent-tour_skipped': HelpAgentTourSkipped
}

interface HelpAgentOpened {
  route: string
}

interface HelpAgentFeedbackSubmitted {
  ratings: Record<string, number>
}

interface HelpAgentTourEvent {
  tour_id: string
}

interface HelpAgentTourSkipped {
  tour_id: string
  step: number
}

/**
 * Type-safe, error-guarded wrapper around posthog.capture().
 *
 * Centralises all analytics calls so that:
 * - A PostHog failure never breaks user workflows
 * - Every call site gets compile-time property validation
 * - Event names are constrained to the known taxonomy
 */
export function trackEvent<K extends keyof AnalyticsEventMap>(
  event: K,
  properties: AnalyticsEventMap[K] | null | undefined,
  options?: { send_instantly?: boolean; transport?: 'XHR' | 'sendBeacon' }
): void {
  try {
    posthog.capture(event, properties as Record<string, unknown>, options)
  } catch (error) {
    console.warn('Analytics tracking failed:', error)
  }
}

/**
 * Set persistent user properties that will be attached to all future events.
 * Use for properties like user_tier, activation_status.
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  try {
    posthog.setPersonProperties(properties)
  } catch (error) {
    console.warn('Analytics setPersonProperties failed:', error)
  }
}

/**
 * Set user properties that are only written once (first time only).
 * Use for immutable properties like signup_date that should never be overwritten.
 * Uses PostHog's setPersonProperties with the second argument for $set_once semantics.
 */
export function setUserPropertiesOnce(properties: Record<string, unknown>): void {
  try {
    posthog.setPersonProperties({}, properties)
  } catch (error) {
    console.warn('Analytics setPersonPropertiesOnce failed:', error)
  }
}
